import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Dialog } from '@/components/ui/Dialog';
import { useSecrets, useSecretActions } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';

const SERVICES = [
  'anthropic',
  'openai',
  'hubspot',
  'emailsleuth',
  'successai',
  'instantly',
  'dataforseo',
  'twocaptcha',
  'browserless',
  'firecrawl',
  'searxng',
  'packetstream',
];

export default function SecretsPage() {
  const secrets = useSecrets();
  const { upsert, remove } = useSecretActions();
  const [show, setShow] = useState(false);
  const [kind, setKind] = useState('api_key');
  const [service, setService] = useState(SERVICES[0]!);
  const [value, setValue] = useState('');

  async function handleSubmit() {
    if (!service.trim() || !kind.trim() || !value.trim()) return;
    await upsert.mutateAsync({ name: kind.trim(), service, value: value.trim() });
    setKind('api_key');
    setValue('');
    setShow(false);
    toast.success('Secret enregistré');
  }

  async function handleDelete(id: string, label: string) {
    if (!confirm(`Supprimer le secret "${label}" ?`)) return;
    await remove.mutateAsync(id);
    toast.success('Secret supprimé');
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShow(true)}>
          <Plus size={14} /> Nouveau secret
        </Button>
      </div>

      <Card>
        <CardHeader title="Secrets services" subtitle="Chiffrés AES-GCM avec MASTER_KEY" />
        {secrets.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !secrets.data?.length ? (
          <EmptyState title="Aucun secret enregistré" />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-brand-border">
              <tr>
                <th className="th">Service</th>
                <th className="th">Kind</th>
                <th className="th">Valeur</th>
                <th className="th">Rotation</th>
                <th className="th">Créé</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {secrets.data.map((s) => (
                <tr key={s.id}>
                  <td className="td font-medium text-brand-dark">{s.service}</td>
                  <td className="td">
                    <span className="font-mono text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                      {s.kind}
                    </span>
                  </td>
                  <td className="td font-mono text-xs">{s.masked_value}</td>
                  <td className="td">{formatDateTime(s.rotated_at)}</td>
                  <td className="td">{formatDateTime(s.created_at)}</td>
                  <td className="td">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(s.id, `${s.service}:${s.kind}`)}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog
        open={show}
        onClose={() => setShow(false)}
        title="Ajouter un secret"
        description="Stocké chiffré et jamais réaffiché en clair."
        footer={
          <>
            <Button variant="ghost" onClick={() => setShow(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} loading={upsert.isPending}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Service</Label>
            <Select value={service} onChange={(e) => setService(e.target.value)}>
              {SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Kind</Label>
            <Input
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              placeholder="api_key"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Ex : <code>api_key</code>, <code>base_url</code>, <code>login</code>,{' '}
              <code>password</code>, <code>webhook_secret</code>…
            </p>
          </div>
          <div>
            <Label>Valeur</Label>
            <Input
              type="password"
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="sk-…"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Si le couple (service, kind) existe déjà, sa valeur est rotée.
            </p>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
